import type { ReactNode } from 'react';
import { useTheme } from '../theme/ThemeContext';
import { getAnalysisPanelTheme } from '../theme/themeStyles';
import {
  type AlertFeedbackRecord,
  type MatchedFarmAlertFeedbacks,
  buildFeedbackTimeSeriesPlan,
  formatFeedbackAlertType,
  formatFeedbackFlag,
  formatFeedbackSource,
  getFeedbackLonLat,
  formatInterventionLabel,
  getFeedbackActionDate,
  getFeedbackActionsPlanned,
  getFeedbackActionsTaken,
  getFeedbackProofPhotos,
  normalizeObservationDay,
  parseFeedbackInterventionData,
  resolveAlertFeedbackPhotoUrl,
} from '../utils/farmMobileAlerts';

type PanelTheme = ReturnType<typeof getAnalysisPanelTheme>;

function formatFeedbackWhen(iso: string): string {
  const parsed = new Date(iso);
  if (Number.isNaN(parsed.getTime())) return iso;
  const time = parsed.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  const today = new Date();
  if (parsed.toDateString() === today.toDateString()) return `Today, ${time}`;
  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);
  if (parsed.toDateString() === yesterday.toDateString()) return `Yesterday, ${time}`;
  return parsed.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function formatActionDay(iso: string | null): string | null {
  if (!iso) return null;
  const parsed = new Date(`${iso}T12:00:00`);
  if (Number.isNaN(parsed.getTime())) return iso;
  return parsed.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function farmerInitials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return 'F';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0] ?? ''}${parts[parts.length - 1]?.[0] ?? ''}`.toUpperCase();
}

function chipClass(compact: boolean, tone: string): string {
  return `inline-flex items-center rounded-full border px-1.5 py-px font-semibold leading-none tracking-wide ${
    compact ? 'text-[8px]' : 'text-[9px]'
  } ${tone}`;
}

function flagTone(value: string, isDark: boolean): string {
  if (value === 'Yes') {
    return isDark
      ? 'border-emerald-500/35 bg-emerald-500/15 text-emerald-200'
      : 'border-[#00854033] bg-[#00854014] text-[#008540]';
  }
  if (value === 'No') {
    return isDark
      ? 'border-rose-500/35 bg-rose-500/15 text-rose-200'
      : 'border-rose-200 bg-rose-50 text-rose-700';
  }
  return isDark
    ? 'border-amber-500/35 bg-amber-500/15 text-amber-200'
    : 'border-amber-200 bg-amber-50 text-amber-800';
}

function alertTypeTone(label: string, isDark: boolean): string {
  const key = label.toLowerCase();
  if (key.includes('moisture') || key.includes('ndmi')) {
    return isDark
      ? 'border-sky-500/35 bg-sky-500/15 text-sky-200'
      : 'border-sky-200 bg-sky-50 text-sky-700';
  }
  if (key.includes('drought')) {
    return isDark
      ? 'border-amber-500/35 bg-amber-500/15 text-amber-200'
      : 'border-amber-200 bg-amber-50 text-amber-800';
  }
  if (key.includes('pest')) {
    return isDark
      ? 'border-orange-500/35 bg-orange-500/15 text-orange-200'
      : 'border-orange-200 bg-orange-50 text-orange-800';
  }
  return isDark
    ? 'border-emerald-500/35 bg-emerald-500/15 text-emerald-200'
    : 'border-[#00854033] bg-[#00854014] text-[#008540]';
}

function neutralChipTone(isDark: boolean): string {
  return isDark
    ? 'border-gray-600 bg-gray-800/80 text-gray-300'
    : 'border-gray-200 bg-gray-50 text-gray-600';
}

function FeedbackRating({
  rating,
  compact,
  isDark,
}: {
  rating: number;
  compact: boolean;
  isDark: boolean;
}) {
  const filled = Math.max(0, Math.min(5, Math.round(rating)));
  return (
    <span
      className="inline-flex items-center gap-px"
      aria-label={`Rated ${filled} out of 5`}
    >
      {[1, 2, 3, 4, 5].map((star) => (
        <i
          key={star}
          className={`${star <= filled ? 'ri-star-fill text-amber-400' : `ri-star-line ${isDark ? 'text-gray-600' : 'text-gray-300'}`} ${
            compact ? 'text-[11px]' : 'text-sm'
          }`}
          aria-hidden
        />
      ))}
    </span>
  );
}

function FeedbackFact({
  icon,
  label,
  value,
  compact,
  ap,
}: {
  icon: string;
  label: string;
  value: string;
  compact: boolean;
  ap: PanelTheme;
}) {
  return (
    <div className="min-w-0">
      <p
        className={`flex items-center gap-1 font-medium uppercase tracking-wide ${ap.faint} ${
          compact ? 'text-[8px]' : 'text-[9px]'
        }`}
      >
        <i className={`${icon} text-[10px]`} aria-hidden />
        {label}
      </p>
      <p className={`mt-0.5 leading-snug ${ap.subheading} ${compact ? 'text-[10px]' : 'text-[11px]'}`}>
        {value}
      </p>
    </div>
  );
}

function ActionBlock({
  title,
  icon,
  items,
  accent,
  compact,
  ap,
  isDark,
}: {
  title: string;
  icon: string;
  items: string[];
  accent: 'planned' | 'taken';
  compact: boolean;
  ap: PanelTheme;
  isDark: boolean;
}) {
  const bar =
    accent === 'taken'
      ? isDark
        ? 'border-emerald-400/70 bg-emerald-500/[0.08]'
        : 'border-[#008540] bg-[#0085400A]'
      : isDark
        ? 'border-sky-400/70 bg-sky-500/[0.08]'
        : 'border-sky-500 bg-sky-50/80';
  const iconColor = accent === 'taken' ? ap.accentText : isDark ? 'text-sky-300' : 'text-sky-700';

  return (
    <div className={`rounded-lg border-l-[3px] px-2.5 py-2 ${bar}`}>
      <p
        className={`mb-1.5 font-semibold uppercase tracking-wide ${ap.faint} ${
          compact ? 'text-[8px]' : 'text-[9px]'
        }`}
      >
        {title}
      </p>
      <ul className={`space-y-1 ${ap.heading} ${compact ? 'text-[11px]' : 'text-[13px]'}`}>
        {items.map((action) => (
          <li key={action} className="flex items-start gap-1.5 leading-snug">
            <i className={`${icon} mt-0.5 shrink-0 ${iconColor}`} aria-hidden />
            <span>{action}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function FarmAlertFeedbackCard({
  feedback,
  compact,
  onCheckBeforeAfter,
  onZoomToLocation,
}: {
  feedback: AlertFeedbackRecord;
  compact: boolean;
  onCheckBeforeAfter?: (feedback: AlertFeedbackRecord) => void;
  onZoomToLocation?: (lon: number, lat: number) => void;
}) {
  const { isDark } = useTheme();
  const ap = getAnalysisPanelTheme(isDark);
  const farmerName =
    feedback.user?.full_name?.trim() ||
    [feedback.user?.first_name, feedback.user?.last_name].filter(Boolean).join(' ').trim() ||
    'Farmer';
  const accurate = formatFeedbackFlag(feedback.is_accurate);
  const helpful = formatFeedbackFlag(feedback.is_helpful);
  const intervention = parseFeedbackInterventionData(feedback);
  const comment = feedback.comment?.trim() ?? '';
  const details = String(intervention?.details ?? '').trim();
  const showComment = Boolean(comment && comment.toLowerCase() !== details.toLowerCase());
  const noteText = formatInterventionLabel(showComment ? comment : details || comment);
  const photos = getFeedbackProofPhotos(feedback);
  const actionsTaken = getFeedbackActionsTaken(feedback).map((action) =>
    formatInterventionLabel(action)
  );
  const actionsPlanned = getFeedbackActionsPlanned(feedback).map((action) =>
    formatInterventionLabel(action)
  );
  const actionDate = getFeedbackActionDate(feedback);
  const actionDateLabel = formatActionDay(actionDate);
  const effect = formatInterventionLabel(intervention?.effect);
  const followed = formatInterventionLabel(intervention?.followed_recommendation);
  const responsePlan = formatInterventionLabel(intervention?.response_plan);
  const fieldObservation = formatInterventionLabel(intervention?.field_observation);
  const feedbackStage = formatInterventionLabel(intervention?.feedback_stage);
  const forecastKind = formatInterventionLabel(intervention?.forecast_kind);
  const forecastDateLabel = formatActionDay(normalizeObservationDay(intervention?.forecast_date));
  const sourceLabel = formatFeedbackSource(feedback.source);
  const alertTypeLabel = formatFeedbackAlertType(
    feedback.alert_type || feedback.alert?.alert_type
  );
  const observationDateLabel = formatActionDay(
    normalizeObservationDay(feedback.alert?.observation_date)
  );
  const farmerMobile = feedback.user?.mobile?.trim() || '';
  const farmName = feedback.farm?.name?.trim() || '';
  const farmAreaRaw = Number(feedback.farm?.total_land_area);
  const farmArea =
    feedback.farm?.total_land_area != null &&
    String(feedback.farm.total_land_area).trim() !== '' &&
    Number.isFinite(farmAreaRaw)
      ? `${farmAreaRaw.toFixed(2)} ha`
      : '';
  const lonLat = getFeedbackLonLat(feedback);
  const tsPlan = onCheckBeforeAfter ? buildFeedbackTimeSeriesPlan(feedback) : null;
  const rating =
    feedback.rating != null && Number.isFinite(Number(feedback.rating))
      ? Number(feedback.rating)
      : null;
  const identityBits = [farmName, farmArea, farmerMobile].filter(Boolean);
  const facts: { icon: string; label: string; value: string }[] = [
    observationDateLabel ? { icon: 'ri-eye-line', label: 'Observed', value: observationDateLabel } : null,
    actionDateLabel ? { icon: 'ri-calendar-event-line', label: 'Action date', value: actionDateLabel } : null,
    forecastDateLabel ? { icon: 'ri-cloud-line', label: 'Forecast', value: forecastDateLabel } : null,
    forecastKind ? { icon: 'ri-cloudy-line', label: 'Kind', value: forecastKind } : null,
    effect ? { icon: 'ri-pulse-line', label: 'Effect', value: effect } : null,
    followed ? { icon: 'ri-user-follow-line', label: 'Followed', value: followed } : null,
    responsePlan ? { icon: 'ri-route-line', label: 'Plan', value: responsePlan } : null,
    fieldObservation
      ? { icon: 'ri-plant-line', label: 'Field', value: fieldObservation }
      : null,
    feedback.data_value != null && String(feedback.data_value).trim() !== ''
      ? { icon: 'ri-bar-chart-2-line', label: 'Alert value', value: String(feedback.data_value) }
      : null,
  ].filter((fact): fact is { icon: string; label: string; value: string } => Boolean(fact));

  const iconBtn = `inline-flex shrink-0 items-center justify-center rounded-full border transition-[transform,background-color,border-color,color] duration-150 ease-out active:scale-[0.97] disabled:cursor-not-allowed disabled:opacity-50 ${
    compact ? 'h-7 w-7 text-sm' : 'h-8 w-8 text-base'
  } ${ap.mobileBtn}`;

  return (
    <article
      className={`rounded-xl border ${ap.cardSoft} ${compact ? 'p-2.5' : 'p-3.5'}`}
    >
      <div className="flex items-start gap-2.5">
        <div
          className={`flex shrink-0 items-center justify-center rounded-full font-semibold ${ap.iconWrapVeg} ${
            compact ? 'h-8 w-8 text-[10px]' : 'h-9 w-9 text-xs'
          }`}
          aria-hidden
        >
          {farmerInitials(farmerName)}
        </div>

        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <p className={`truncate font-semibold ${ap.heading} ${compact ? 'text-xs' : 'text-sm'}`}>
                {farmerName}
              </p>
              {identityBits.length > 0 && (
                <p className={`mt-0.5 truncate ${ap.muted} ${compact ? 'text-[9px]' : 'text-[10px]'}`}>
                  {identityBits.join(' · ')}
                </p>
              )}
            </div>
            <div className="flex shrink-0 items-center gap-1">
              {lonLat && (
                <button
                  type="button"
                  onClick={() => onZoomToLocation?.(lonLat[0], lonLat[1])}
                  disabled={!onZoomToLocation}
                  title="Show on map"
                  aria-label="Zoom map to submission location"
                  className={iconBtn}
                >
                  <i className="ri-map-pin-2-fill" aria-hidden />
                </button>
              )}
            </div>
          </div>
          <p
            className={`mt-1 flex items-center gap-1 ${ap.faint} ${compact ? 'text-[9px]' : 'text-[10px]'}`}
          >
            <i className="ri-time-line" aria-hidden />
            {formatFeedbackWhen(feedback.created_at)}
          </p>
        </div>
      </div>

      <div className="mt-2.5 flex flex-wrap items-center gap-1">
        {alertTypeLabel && (
          <span className={chipClass(compact, alertTypeTone(alertTypeLabel, isDark))}>
            {alertTypeLabel}
          </span>
        )}
        {feedbackStage && (
          <span className={chipClass(compact, neutralChipTone(isDark))}>{feedbackStage}</span>
        )}
        {sourceLabel && (
          <span className={chipClass(compact, neutralChipTone(isDark))}>{sourceLabel}</span>
        )}
        {accurate && (
          <span className={chipClass(compact, flagTone(accurate, isDark))}>Accurate · {accurate}</span>
        )}
        {helpful && (
          <span className={chipClass(compact, flagTone(helpful, isDark))}>Helpful · {helpful}</span>
        )}
        {rating != null && <FeedbackRating rating={rating} compact={compact} isDark={isDark} />}
      </div>

      {(actionsPlanned.length > 0 || actionsTaken.length > 0) && (
        <div className={`mt-2.5 ${compact ? 'space-y-1.5' : 'space-y-2'}`}>
          {actionsPlanned.length > 0 && (
            <ActionBlock
              title="Action planned"
              icon="ri-calendar-check-line"
              items={actionsPlanned}
              accent="planned"
              compact={compact}
              ap={ap}
              isDark={isDark}
            />
          )}
          {actionsTaken.length > 0 && (
            <ActionBlock
              title="Action taken"
              icon="ri-checkbox-circle-line"
              items={actionsTaken}
              accent="taken"
              compact={compact}
              ap={ap}
              isDark={isDark}
            />
          )}
        </div>
      )}

      {noteText && (
        <p
          className={`mt-2.5 leading-relaxed ${ap.body} ${compact ? 'text-[11px]' : 'text-[13px]'}`}
        >
          {noteText}
        </p>
      )}

      {photos.length > 0 && (
        <div className="mt-2.5 flex gap-1.5 overflow-x-auto pb-0.5">
          {photos.map((photo) => (
            <a
              key={photo}
              href={resolveAlertFeedbackPhotoUrl(photo)}
              target="_blank"
              rel="noopener noreferrer"
              title="Open photo"
              className={`block shrink-0 overflow-hidden rounded-lg border ${ap.cardSolid} transition-transform duration-150 ease-out active:scale-[0.97] [@media(hover:hover)_and_(pointer:fine)]:hover:scale-[1.03]`}
            >
              <img
                src={resolveAlertFeedbackPhotoUrl(photo)}
                alt="Farmer feedback photo"
                className={`object-cover ${compact ? 'h-[4.5rem] w-[4.5rem]' : 'h-20 w-20'}`}
                loading="lazy"
              />
            </a>
          ))}
        </div>
      )}

      {facts.length > 0 && (
        <div
          className={`mt-2.5 grid grid-cols-2 gap-x-3 gap-y-2 border-t pt-2.5 ${
            isDark ? 'border-[#2d3748]' : 'border-gray-100'
          }`}
        >
          {facts.map((fact) => (
            <FeedbackFact
              key={`${fact.label}-${fact.value}`}
              icon={fact.icon}
              label={fact.label}
              value={fact.value}
              compact={compact}
              ap={ap}
            />
          ))}
        </div>
      )}

      {onCheckBeforeAfter && tsPlan && (
        <div
          className={`mt-2.5 border-t pt-2.5 ${isDark ? 'border-[#2d3748]' : 'border-gray-100'}`}
        >
          <button
            type="button"
            onClick={() => onCheckBeforeAfter(feedback)}
            className={`inline-flex w-full items-center justify-center gap-1.5 rounded-lg px-3 py-1.5 font-medium transition-transform duration-150 ease-out active:scale-[0.97] ${ap.primaryBtn} ${
              compact ? 'text-[10px]' : 'text-[11px]'
            }`}
          >
            <i className="ri-compare-line" aria-hidden />
            Compare {tsPlan.indexLabel} on map
          </button>
        </div>
      )}
    </article>
  );
}

function FeedbackStatusCard({
  compact,
  ap,
  children,
}: {
  compact: boolean;
  ap: PanelTheme;
  children: ReactNode;
}) {
  return (
    <div
      className={`flex items-center gap-2 rounded-xl border px-3 py-2.5 ${ap.cardSoft} ${ap.muted} ${
        compact ? 'text-[10px]' : 'text-xs'
      }`}
    >
      {children}
    </div>
  );
}

type FarmAlertFeedbackListProps = {
  loading: boolean;
  error: string | null;
  feedbacks: AlertFeedbackRecord[];
  compact?: boolean;
  onCheckBeforeAfter?: (feedback: AlertFeedbackRecord) => void;
  onZoomToLocation?: (lon: number, lat: number) => void;
};

export function FarmAlertFeedbackList({
  loading,
  error,
  feedbacks,
  compact = false,
  onCheckBeforeAfter,
  onZoomToLocation,
}: FarmAlertFeedbackListProps) {
  const { isDark } = useTheme();
  const ap = getAnalysisPanelTheme(isDark);

  if (loading) {
    return (
      <FeedbackStatusCard compact={compact} ap={ap}>
        <div className="h-3.5 w-3.5 animate-spin rounded-full border border-[#008540CC] border-t-transparent" />
        <span>Loading farmer feedback…</span>
      </FeedbackStatusCard>
    );
  }

  if (error) {
    return (
      <p role="alert" className="text-[11px] leading-snug text-red-500">
        {error}
      </p>
    );
  }

  if (feedbacks.length === 0) {
    return (
      <FeedbackStatusCard compact={compact} ap={ap}>
        <i className="ri-chat-off-line shrink-0" aria-hidden />
        <span>No farmer feedback yet for this alert.</span>
      </FeedbackStatusCard>
    );
  }

  return (
    <div className={compact ? 'space-y-2' : 'space-y-2.5'}>
      {feedbacks.map((feedback) => (
        <FarmAlertFeedbackCard
          key={feedback.id}
          feedback={feedback}
          compact={compact}
          onCheckBeforeAfter={onCheckBeforeAfter}
          onZoomToLocation={onZoomToLocation}
        />
      ))}
    </div>
  );
}

function FeedbackGroup({
  title,
  icon,
  feedbacks,
  compact,
  onCheckBeforeAfter,
  onZoomToLocation,
}: {
  title: string;
  icon: string;
  feedbacks: AlertFeedbackRecord[];
  compact: boolean;
  onCheckBeforeAfter?: (feedback: AlertFeedbackRecord) => void;
  onZoomToLocation?: (lon: number, lat: number) => void;
}) {
  const { isDark } = useTheme();
  const ap = getAnalysisPanelTheme(isDark);

  return (
    <div className={compact ? 'space-y-1.5' : 'space-y-2'}>
      <p
        className={`font-semibold uppercase tracking-wider ${ap.faint} ${
          compact ? 'text-[9px]' : 'text-[10px]'
        }`}
      >
        <i className={`${icon} mr-1`} aria-hidden />
        {title}
      </p>
      {feedbacks.length === 0 ? (
        <FeedbackStatusCard compact={compact} ap={ap}>
          <i className="ri-chat-off-line shrink-0" aria-hidden />
          <span>No farmer feedback yet for this alert.</span>
        </FeedbackStatusCard>
      ) : (
        feedbacks.map((feedback) => (
          <FarmAlertFeedbackCard
            key={feedback.id}
            feedback={feedback}
            compact={compact}
            onCheckBeforeAfter={onCheckBeforeAfter}
            onZoomToLocation={onZoomToLocation}
          />
        ))
      )}
    </div>
  );
}

type FarmAlertFeedbacksSectionProps = {
  loading: boolean;
  error: string | null;
  feedbacks: MatchedFarmAlertFeedbacks | null;
  showVegetation: boolean;
  showMoisture: boolean;
  compact?: boolean;
};

export default function FarmAlertFeedbacksSection({
  loading,
  error,
  feedbacks,
  showVegetation,
  showMoisture,
  compact = false,
}: FarmAlertFeedbacksSectionProps) {
  const { isDark } = useTheme();
  const ap = getAnalysisPanelTheme(isDark);

  if (loading) {
    return (
      <div className="mb-2">
        <FeedbackStatusCard compact={compact} ap={ap}>
          <div className="h-3.5 w-3.5 animate-spin rounded-full border border-[#008540CC] border-t-transparent" />
          <span>Loading farmer feedback…</span>
        </FeedbackStatusCard>
      </div>
    );
  }

  if (error) {
    return (
      <p role="alert" className="mb-2 text-[11px] leading-snug text-red-500">
        {error}
      </p>
    );
  }

  if (!feedbacks) return null;

  const totalCount = feedbacks.vegetation.length + feedbacks.moisture.length;

  return (
    <div className={`mb-2 rounded-xl border ${ap.cardSofter} ${compact ? 'p-2' : 'p-3'}`}>
      <div className="mb-2 flex items-center justify-between gap-2">
        <p className={`font-semibold ${ap.heading} ${compact ? 'text-xs' : 'text-sm'}`}>
          Farmer feedback
        </p>
        <span
          className={`rounded-full border px-2 py-0.5 font-semibold ${ap.cardSoft} ${
            compact ? 'text-[9px]' : 'text-[10px]'
          } ${ap.subheading}`}
        >
          {totalCount} {totalCount === 1 ? 'match' : 'matches'}
        </span>
      </div>

      <div className={compact ? 'space-y-2' : 'space-y-3'}>
        {showVegetation && (
          <FeedbackGroup
            title="Vegetation"
            icon="ri-leaf-line"
            feedbacks={feedbacks.vegetation}
            compact={compact}
          />
        )}
        {showMoisture && (
          <FeedbackGroup
            title="Moisture"
            icon="ri-drop-fill"
            feedbacks={feedbacks.moisture}
            compact={compact}
          />
        )}
      </div>
    </div>
  );
}
